import { useState, useEffect, useRef, useCallback } from "react";

/**
 * Hook genérico para llamadas a la API.
 *
 * @param {Function} fetcher  - función que devuelve una promesa (ej. () => api.getClient("Alojamiento2"))
 * @param {Array} deps        - dependencias (cambio dispara refetch)
 * @param {Object} opts       - { pollMs: 60000, skip: false, initial: null }
 *
 * Devuelve { data, loading, error, refetch }.
 */
export function useApi(fetcher, deps = [], opts = {}) {
  const { pollMs = 0, skip = false, initial = null } = opts;
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  const run = useCallback(async () => {
    if (skip) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await fetcher();
      if (mounted.current) {
        setData(result);
        setError(null);
      }
    } catch (e) {
      if (mounted.current) setError(e.message || String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
    // eslint-disable-next-line
  }, [...deps, skip]);

  useEffect(() => {
    mounted.current = true;
    run();
    let interval;
    if (pollMs > 0) interval = setInterval(run, pollMs);
    return () => {
      mounted.current = false;
      if (interval) clearInterval(interval);
    };
  }, [run, pollMs]);

  return { data, loading, error, refetch: run };
}

export default useApi;
